import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { qbankApi } from '../lib/questionBankApi';
import type { NamedOption, QuestionPage } from '../lib/questionBankApi';
import {
  SCAN_WARNING, SOURCE_KINDS, extractPdfPages, judgeText, kindLabel, pagesFromTypedText, sourcesApi,
} from '../lib/sourcesApi';
import type { SourceKind, SourcePage, SourceSummary } from '../lib/sourcesApi';
import { StatTile } from '../components/charts';

/**
 * SOURCE MATERIAL — the chapters the assistant is allowed to write questions from.
 *
 * ⚠ THE PAGE NUMBER IS THE PRODUCT. A question drafted from a chapter is worth having only if the
 * head of department can open the book at the page it came from and see that it is fair. Everything
 * on this screen — extracting in the browser rather than on the server, storing pages rather than
 * one blob of text, the reader at the bottom of each card — exists so that citation stays checkable.
 *
 * ⚠ THE MOST LIKELY UPLOAD IN AN INDIAN SCHOOL IS A PHOTOCOPY, AND IT CONTAINS NO TEXT. A scanned
 * chapter is a stack of photographs; pdf.js finds nothing in it and the honest answer is to say so
 * before anything is stored, not to file an empty document and let the assistant fail later with
 * "no relevant material found".
 */

/** What is on screen before it is saved: the extracted pages plus what the teacher is calling them. */
interface Draft {
  pages: SourcePage[];
  title: string;
  attribution: string;
  kind: SourceKind;
  subjectId: string;
  classLabel: string;
  unitId: string;
  /** Where it came from, for the line above the preview. Null for typed notes. */
  fileName: string | null;
}

type AddMode = 'pdf' | 'typed';
type Scope = 'all' | 'mine' | 'shared';

const CLASS_LABELS = ['VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

export function SourcesPage() {
  const [list, setList] = useState<SourceSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const [institutionId, setInstitutionId] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [scope, setScope] = useState<Scope>('all');

  const [mode, setMode] = useState<AddMode>('pdf');
  const [typed, setTyped] = useState('');
  const [extracting, setExtracting] = useState<{ name: string; done: number; total: number } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editingPage, setEditingPage] = useState(0);
  const [saving, setSaving] = useState(false);

  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  // Bumped whenever a new extraction starts. The running one compares against it and stops, so
  // picking a second file does not leave two loops racing to fill the same draft.
  const runRef = useRef(0);

  /* ⚠ Subjects, units and institutions come from the question-bank list endpoint rather than one of
     their own. They are the SAME lists a question is tagged with, and a source tagged against a
     different set of subjects could never be matched to the gap it is supposed to fill. `take: 1`
     because only the option lists are wanted. Re-fetched when the unit scope changes, because
     `units` is per-subject. */
  const [opts, setOpts] = useState<QuestionPage | null>(null);
  const unitScope = draft?.subjectId || subjectFilter;
  useEffect(() => {
    let alive = true;
    qbankApi
      .list({ skip: '0', take: '1', ...(unitScope ? { subjectId: unitScope } : {}) })
      .then((r) => {
        if (!alive) return;
        setOpts(r);
        setInstitutionId((cur) => cur || r.institutionId || '');
      })
      .catch(() => { /* the library still lists without the option lists */ });
    return () => { alive = false; };
  }, [unitScope]);

  const load = useCallback(() => {
    setLoading(true);
    return sourcesApi
      .list(institutionId || null, subjectFilter || null)
      .then((r) => { setList(r); setFailed(false); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [institutionId, subjectFilter]);

  useEffect(() => { void load(); }, [load]);

  // ------------------------------------------------------------------ extraction

  const onFile = (file: File) => {
    setProblem(null);
    setFlash(null);
    const run = ++runRef.current;
    setDraft(null);
    setExtracting({ name: file.name, done: 0, total: 0 });

    extractPdfPages(
      file,
      (done, total) => { if (runRef.current === run) setExtracting({ name: file.name, done, total }); },
      () => runRef.current !== run,
    )
      .then((pages) => {
        if (runRef.current !== run) return;
        setDraft({
          pages,
          title: titleFromFile(file.name),
          attribution: '',
          kind: 'SchoolMaterial',
          subjectId: subjectFilter,
          classLabel: '',
          unitId: '',
          fileName: file.name,
        });
        // Open on the first page that actually has something on it — on a part-scanned chapter
        // that is the difference between "it worked" and a blank box.
        setEditingPage(Math.max(0, pages.findIndex((p) => p.text.trim().length > 0)));
      })
      .catch((e: unknown) => {
        if (runRef.current !== run) return;
        setProblem(readingProblem(e));
      })
      .finally(() => { if (runRef.current === run) setExtracting(null); });
  };

  const useTypedText = () => {
    const pages = pagesFromTypedText(typed);
    if (pages.length === 0) { setProblem('There is nothing in the box yet.'); return; }
    setProblem(null);
    setDraft({
      pages,
      title: '',
      attribution: '',
      kind: 'SchoolMaterial',
      subjectId: subjectFilter,
      classLabel: '',
      unitId: '',
      fileName: null,
    });
    setEditingPage(0);
  };

  const discard = () => {
    runRef.current++;          // stops an extraction still in flight
    setDraft(null);
    setExtracting(null);
    setProblem(null);
    setEditingPage(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  // ⚠ Judged on the CURRENT pages, not on what came out of the file. A teacher who types the two
  // missing pages in by hand must see the warning go away — otherwise it reads as a stuck error.
  const verdict = useMemo(() => judgeText(draft?.pages ?? []), [draft]);

  const save = () => {
    if (!draft) return;
    setSaving(true);
    setProblem(null);
    sourcesApi
      .create({
        institutionId: institutionId || null,
        title: draft.title.trim(),
        attribution: draft.attribution.trim() || null,
        kind: draft.kind,
        subjectId: draft.subjectId || null,
        classLabel: draft.classLabel || null,
        unitId: draft.unitId || null,
        // Empty pages are dropped rather than stored. A blank page is not a page of the chapter,
        // and storing it means the assistant can cite one.
        pages: draft.pages.filter((p) => p.text.trim().length > 0)
          .map((p) => ({ page: p.page, text: p.text.trim() })),
      })
      .then((r) => {
        setFlash(`Saved "${draft.title.trim()}" — ${count(r.chunks)} passage${r.chunks === 1 ? '' : 's'} the assistant can quote from.`);
        discard();
        setTyped('');
        return load();
      })
      .catch((e: unknown) => setProblem(messageOf(e) ?? 'Could not save that chapter.'))
      .finally(() => setSaving(false));
  };

  const remove = (id: string) => {
    setConfirmId(null);
    sourcesApi
      .retire(id)
      .then(() => { setFlash('Removed. Questions already written from it keep their citation.'); return load(); })
      .catch((e: unknown) => setProblem(messageOf(e) ?? 'Could not remove that chapter.'));
  };

  // ------------------------------------------------------------------ render

  const shown = (list ?? []).filter((s) =>
    scope === 'all' || (scope === 'shared' ? s.shared : !s.shared));

  const mine = (list ?? []).filter((s) => !s.shared);
  const totalPages = (list ?? []).reduce((n, s) => n + s.pageCount, 0);

  const canSave = draft !== null && draft.title.trim().length > 0 && !verdict.blank && !saving;

  return (
    <div className="src">
      <header className="src-head">
        <h1>Source material</h1>
        <p className="src-sub">
          The chapters and notes Eduvora is allowed to write questions from. Every draft it produces
          names the page it came from, so a question can be checked against the book before it goes
          on a paper.
        </p>
      </header>

      <div className="src-tiles">
        <StatTile label="Chapters" value={count(list?.length ?? 0)} foot="available to this school" />
        <StatTile label="Uploaded here" value={count(mine.length)} foot="your school's own material" />
        <StatTile label="Pages" value={count(totalPages)} foot="readable, with real page numbers" />
      </div>

      {flash && <div className="src-flash">{flash}</div>}
      {problem && <div className="src-bad">{problem}</div>}

      {/* ---------------------------------------------------------------- add */}
      <div className="card src-add">
        <div className="src-add-head">
          <h2>Add a chapter</h2>
          <div className="src-tabs" role="tablist">
            {(['pdf', 'typed'] as const).map((m) => (
              <button key={m} role="tab" aria-selected={mode === m}
                className={`src-tab ${mode === m ? 'is-on' : ''}`}
                onClick={() => { setMode(m); setProblem(null); }}>
                {m === 'pdf' ? 'From a PDF' : 'Type or paste it'}
              </button>
            ))}
          </div>
        </div>

        {mode === 'pdf' ? (
          <>
            <p className="src-sub">
              The file is read here, in this browser — it is never uploaded. Only the text and its
              page numbers are sent, which is why a 30 MB chapter takes seconds rather than an
              afternoon.
            </p>
            <label className="src-pick">
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                disabled={extracting !== null}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  // ⚠ Cleared immediately: without this, choosing the SAME file again fires no
                  // change event at all and the screen sits there looking broken.
                  e.target.value = '';
                  if (f) onFile(f);
                }}
              />
              <span className="btn btn-accent">Choose a PDF</span>
            </label>
          </>
        ) : (
          <>
            <p className="src-sub">
              For a teacher who has notes rather than a book. Separate each page with a line of three
              dashes (<code>---</code>) so a question can still cite the page it came from.
            </p>
            <textarea
              className="src-paste"
              rows={7}
              value={typed}
              placeholder={'Electric charge is a property of matter…\n\n---\n\nCoulomb’s law states…'}
              onChange={(e) => setTyped(e.target.value)}
            />
            <div className="src-row-end">
              <span className="src-muted">
                {count(pagesFromTypedText(typed).length)} page{pagesFromTypedText(typed).length === 1 ? '' : 's'}
              </span>
              <button className="btn btn-accent" disabled={typed.trim().length === 0} onClick={useTypedText}>
                Use this text
              </button>
            </div>
          </>
        )}

        {extracting && (
          <div className="src-progress" role="status" aria-live="polite">
            <div className="src-progress-line">
              <span>Reading {extracting.name}</span>
              <span className="src-muted">
                {extracting.total > 0
                  ? `${count(extracting.done)} of ${count(extracting.total)} pages`
                  : 'opening the file…'}
              </span>
            </div>
            {/* Real progress, page by page. A forty-page chapter takes a few seconds and a bar that
                does not move is indistinguishable from a crash. */}
            <div className="src-bar">
              <div className="src-bar-fill"
                style={{ width: `${extracting.total > 0 ? (extracting.done / extracting.total) * 100 : 4}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* ---------------------------------------------------------------- draft */}
      {draft && (
        <div className="card src-draft">
          <div className="src-add-head">
            <h2>
              {draft.fileName ? 'What was read' : 'The pages you typed'}
              {draft.fileName && <span className="src-muted"> · {draft.fileName}</span>}
            </h2>
            <span className="src-muted">
              {count(draft.pages.length)} page{draft.pages.length === 1 ? '' : 's'} · {count(verdict.characters)} characters
            </span>
          </div>

          {verdict.looksScanned && (
            <div className="src-scan">
              <strong>{verdict.blank ? 'Nothing readable came out of this file.' : `${count(verdict.emptyPages)} of ${count(verdict.pages)} pages came out empty.`}</strong>
              <p>{SCAN_WARNING}</p>
              <p className="src-muted">
                If only a few pages are missing you can type them into the preview below and save the
                rest.
              </p>
            </div>
          )}

          <div className="src-form">
            <label>
              <span>Title</span>
              <input value={draft.title} placeholder="Chapter 1 — Electric Charges and Fields"
                onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            </label>
            <label>
              <span>Attribution</span>
              <input value={draft.attribution} placeholder="NCERT Physics Part I, Class XII"
                onChange={(e) => setDraft({ ...draft, attribution: e.target.value })} />
            </label>
            <label>
              <span>What it is</span>
              <select value={draft.kind}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value as SourceKind })}>
                {SOURCE_KINDS.map((k) => (
                  <option key={k.name} value={k.name}>{k.label}{k.hint ? ` — ${k.hint}` : ''}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Subject</span>
              <select value={draft.subjectId}
                onChange={(e) => setDraft({ ...draft, subjectId: e.target.value, unitId: '' })}>
                <option value="">Not tied to one</option>
                {(opts?.subjects ?? []).map((s: NamedOption) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label>
              <span>Class</span>
              <select value={draft.classLabel}
                onChange={(e) => setDraft({ ...draft, classLabel: e.target.value })}>
                <option value="">Any class</option>
                {CLASS_LABELS.map((c) => <option key={c} value={c}>Class {c}</option>)}
              </select>
            </label>
            <label>
              <span>Unit</span>
              <select value={draft.unitId} disabled={!draft.subjectId}
                onChange={(e) => setDraft({ ...draft, unitId: e.target.value })}>
                <option value="">{draft.subjectId ? 'Not tied to one' : 'Pick a subject first'}</option>
                {(opts?.units ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </label>
          </div>

          <PageEditor
            pages={draft.pages}
            index={editingPage}
            onIndex={setEditingPage}
            onText={(text) => setDraft({
              ...draft,
              pages: draft.pages.map((p, i) => (i === editingPage ? { ...p, text } : p)),
            })}
          />

          <div className="src-row-end">
            <button className="btn btn-quiet" onClick={discard} disabled={saving}>Discard</button>
            <button className="btn btn-accent" disabled={!canSave} onClick={save}>
              {saving ? 'Saving…' : 'Save to the library'}
            </button>
          </div>
          {draft.title.trim().length === 0 && (
            <p className="src-muted">Give it a title a teacher would recognise before saving.</p>
          )}
          {verdict.blank && (
            <p className="src-muted">There is no text to save yet.</p>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------------- filters */}
      <div className="card src-controls">
        {(opts?.institutions?.length ?? 0) > 1 && (
          <label>
            <span>Institution</span>
            <select value={institutionId} onChange={(e) => setInstitutionId(e.target.value)}>
              {(opts?.institutions ?? []).map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
        )}
        <label>
          <span>Subject</span>
          <select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)}>
            <option value="">All subjects</option>
            {(opts?.subjects ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <div className="src-scope">
          <span>Library</span>
          <div className="src-tabs">
            {([['all', 'Everything'], ['mine', 'This school'], ['shared', 'Platform']] as const).map(([v, label]) => (
              <button key={v} className={`src-tab ${scope === v ? 'is-on' : ''}`} onClick={() => setScope(v)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------------- list */}
      {loading && list === null && <p className="src-muted">Opening the library…</p>}
      {failed && <div className="card src-blank">Could not load the source material.</div>}

      {!failed && list !== null && shown.length === 0 && (
        <div className="card src-blank">
          {list.length === 0
            ? 'Nothing here yet. Add the chapter you are teaching from and Eduvora can write questions out of it.'
            : 'No chapters in that part of the library.'}
        </div>
      )}

      {shown.some((s) => s.shared) && (
        <p className="src-shared-note">
          Chapters marked <strong>Platform library</strong> are Eduvora's own copies of the
          prescribed books, shared with every school. Read them, write questions from them — but
          only Eduvora can withdraw them.
        </p>
      )}

      <div className="src-list">
        {shown.map((s) => (
          <SourceCard
            key={s.id}
            source={s}
            open={openId === s.id}
            onToggle={() => setOpenId(openId === s.id ? null : s.id)}
            confirming={confirmId === s.id}
            onAskRemove={() => setConfirmId(confirmId === s.id ? null : s.id)}
            onRemove={() => remove(s.id)}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ page editor */

/**
 * One page at a time, with every page on a strip above it.
 *
 * ⚠ Not forty textareas. Beyond the rendering cost, the strip is what makes a half-read scan
 * legible at a glance: the empty pages are marked, so "pages 12 to 18 are photographs" is
 * something you can SEE rather than something you find out when a question cites a blank page.
 */
function PageEditor({ pages, index, onIndex, onText }: {
  pages: SourcePage[];
  index: number;
  onIndex: (i: number) => void;
  onText: (text: string) => void;
}) {
  const current = pages[index];
  if (!current) return null;
  return (
    <div className="src-editor">
      <div className="src-strip">
        {pages.map((p, i) => (
          <button key={p.page}
            className={`src-pagechip ${i === index ? 'is-on' : ''} ${p.text.trim().length < 40 ? 'is-empty' : ''}`}
            title={p.text.trim().length < 40 ? `Page ${p.page} — nothing readable` : `Page ${p.page}`}
            onClick={() => onIndex(i)}>
            {p.page}
          </button>
        ))}
      </div>
      <div className="src-editor-head">
        <strong>Page {current.page}</strong>
        <span className="src-muted">
          {current.text.trim().length === 0 ? 'empty' : `${current.text.trim().length.toLocaleString('en-IN')} characters`}
          {' · '}edit anything the reader got wrong
        </span>
      </div>
      <textarea
        className="src-pagetext"
        rows={10}
        value={current.text}
        placeholder="Nothing was readable on this page. Type it in, or leave it out."
        onChange={(e) => onText(e.target.value)}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ one chapter */

function SourceCard({ source, open, onToggle, confirming, onAskRemove, onRemove }: {
  source: SourceSummary;
  open: boolean;
  onToggle: () => void;
  confirming: boolean;
  onAskRemove: () => void;
  onRemove: () => void;
}) {
  return (
    <div className={`card src-item ${source.shared ? 'is-shared' : ''}`}>
      <div className="src-item-head">
        <div className="src-item-main">
          <h3>{source.title}</h3>
          {source.attribution && <p className="src-attr">{source.attribution}</p>}
          <div className="src-badges">
            {source.shared
              ? <span className="src-badge shared">Platform library</span>
              : <span className="src-badge">This school</span>}
            <span className="src-badge">{kindLabel(source.kind)}</span>
            {source.classLabel && <span className="src-badge">Class {source.classLabel}</span>}
            {source.subjectName && <span className="src-badge">{source.subjectName}</span>}
            {source.unitName && <span className="src-badge unit">{source.unitName}</span>}
          </div>
          <p className="src-meta">
            {count(source.pageCount)} page{source.pageCount === 1 ? '' : 's'} ·{' '}
            {count(source.characterCount)} characters · added {pretty(source.createdAt)}
          </p>
        </div>
        <div className="src-item-actions">
          <button className="btn btn-quiet" onClick={onToggle}>{open ? 'Close' : 'Read the pages'}</button>
          {/* ⚠ Shared material belongs to the platform, not to this school: no Remove button at all,
              rather than one that 403s. Why is said ONCE above the list — repeated on every NCERT
              card it was three lines of apology per card on a phone. */}
          {source.shared ? null : confirming ? (
            <button className="btn src-btn-danger" onClick={onRemove}>Remove it?</button>
          ) : (
            <button className="btn btn-quiet" onClick={onAskRemove}>Remove</button>
          )}
        </div>
      </div>

      {open && <PageReader id={source.id} pageCount={source.pageCount} />}
    </div>
  );
}

/* ------------------------------------------------------------------ the reader */

/** How many pages one request asks for. Big enough that paging forward is usually instant, small
    enough that opening a 300-page textbook does not pull megabytes of JSON. */
const WINDOW = 5;

/**
 * ⚠ THIS IS WHY THE FEATURE EXISTS. A citation nobody can open is a claim, not a source. Anything
 * shown here is the stored text — the same text the assistant was given — not the original PDF, so
 * what you read is what it read.
 */
function PageReader({ id, pageCount }: { id: string; pageCount: number }) {
  const [cache, setCache] = useState<Record<number, string>>({});
  const [at, setAt] = useState(1);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [jump, setJump] = useState('1');

  // Which pages have been ASKED for, which is not the same as which have arrived — it is what stops
  // a re-render mid-flight from firing the same request again.
  const asked = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (asked.current.has(at)) return;
    setBusy(true);
    const to = Math.min(at + WINDOW - 1, pageCount);
    for (let p = at; p <= to; p++) asked.current.add(p);
    sourcesApi
      .pages(id, at, to)
      .then((r) => {
        setFailed(false);
        setCache((c) => {
          const next = { ...c };
          // ⚠ Every page in the range is recorded, INCLUDING ones the server did not return —
          // otherwise a page that is genuinely empty is re-fetched every time it is looked at.
          for (let p = at; p <= to; p++) next[p] = '';
          for (const page of r.pages) next[page.page] = page.text;
          return next;
        });
      })
      .catch(() => {
        // Let a failed range be retried rather than remembering it as fetched.
        for (let p = at; p <= to; p++) asked.current.delete(p);
        setFailed(true);
      })
      .finally(() => setBusy(false));

    /* ⚠ NO `alive` FLAG, DELIBERATELY, AND IT IS NOT AN OVERSIGHT. StrictMode runs this effect,
       tears it down, and runs it again on the SAME instance — so the ref still remembers the range
       as asked for while the discarded first response is thrown away. The reader then sits on
       "Fetching…" for ever. The component is still mounted either way, so letting the response land
       is both correct and the only version that works. */
  }, [id, at, pageCount]);

  const go = (p: number) => {
    const next = Math.min(Math.max(1, p), Math.max(1, pageCount));
    setAt(next);
    setJump(String(next));
  };

  const text = cache[at];

  return (
    <div className="src-reader">
      <div className="src-reader-nav">
        <button className="btn btn-quiet" disabled={at <= 1} onClick={() => go(at - 1)}>Previous</button>
        <span className="src-reader-at">
          Page
          <input
            value={jump}
            inputMode="numeric"
            aria-label="Go to page"
            onChange={(e) => setJump(e.target.value)}
            onBlur={() => go(Number(jump) || at)}
            onKeyDown={(e) => { if (e.key === 'Enter') go(Number(jump) || at); }}
          />
          of {count(pageCount)}
        </span>
        <button className="btn btn-quiet" disabled={at >= pageCount} onClick={() => go(at + 1)}>Next</button>
      </div>

      {failed ? (
        <p className="src-muted">Could not fetch that page.</p>
      ) : text === undefined ? (
        <p className="src-muted">{busy ? 'Fetching…' : ''}</p>
      ) : text.trim().length === 0 ? (
        <p className="src-muted">Nothing was stored for page {count(at)}.</p>
      ) : (
        <p className="src-pageview">{text}</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ helpers */

/** ⚠ Indian grouping — a 1,284-page textbook, not a 1284-page one. */
function count(n: number): string {
  return n.toLocaleString('en-IN');
}

/** ⚠ `createdAt` may arrive as a bare date or as a full timestamp. A bare date parsed as-is is read
    as UTC and shows the previous day west of Greenwich, so it is pinned to local midnight. */
function pretty(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "ncert-xii-phy_ch01.pdf" is not a title, but it is a much better start than an empty box. */
function titleFromFile(name: string): string {
  const base = name.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/** pdf.js throws named errors; the password case is common enough to name, and unrecoverable. */
function readingProblem(e: unknown): string {
  const err = e as { name?: string; message?: string } | null;
  if (err?.name === 'PasswordException') {
    return 'That PDF is password-protected. Save an unlocked copy and try again.';
  }
  if (err?.name === 'InvalidPDFException') {
    return 'That file is not a PDF Eduvora can open — it may be damaged or only part-downloaded.';
  }
  return err?.message ? `Could not read that PDF: ${err.message}` : 'Could not read that PDF.';
}

/** ⚠ The API answers a rejected upload with a sentence written for a human. Show it verbatim. */
function messageOf(e: unknown): string | null {
  if (typeof e === 'object' && e !== null && 'message' in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return null;
}
